import { Avatar } from '@components/atoms/Avatar';
import { Button } from '@components/atoms/Button';
import { Card } from '@components/atoms/Card';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { Client } from '@models/client.model';
import React from 'react';
import { ButtonLink } from '@components/atoms/ButtonLink';
import { copyToClipboard } from '@utils/copyToClipboard';

interface ClientHeaderProps {
  client?: Client;
  onEdit: () => void;
  onDelete: () => void;
  // Sprint 25, story-128 (run 5). OWNER/ADMIN/PROJECT_MANAGER only — a SUPERINTENDENT keeps
  // read access to /clients (per the UX doc's ruling) but never sees the write buttons. The
  // server enforces the same restriction regardless (`CLIENT_MANAGEMENT_ROLES`).
  canManage: boolean;
}

export const ClientHeader: React.FC<ClientHeaderProps> = ({
  client,
  onEdit,
  onDelete,
  canManage,
}) => {
  const handleCopyPhone = () => {
    copyToClipboard(client?.phone || '', {
      successMessage: 'Phone copied to clipboard',
      errorMessage: 'No phone to copy',
    });
  };
  return (
    <Card>
      <div className="flex gap-4 flex-col md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex items-start flex-col gap-2">
          <Avatar src={client?.logo} text={client?.name} size="xl" />
          <div className="text-2xl font-bold capitalize text-gray-900 dark:text-gray-100">
            {client?.name}
          </div>
        </div>
        <div className="flex flex-col justify-between gap-3 w-full md:w-auto">
          <div className="flex flex-col">
            <p className="text-sm text-gray-900 dark:text-gray-100">Address</p>
            <p className="text-base text-gray-500 dark:text-gray-300 truncate max-w-full sm:max-w-[200px] lg:max-w-[300px]">
              {client?.address}
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-3 md:gap-6 mt-3 md:mt-0 w-full md:w-auto">
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">Phone</p>
              <p
                onClick={handleCopyPhone}
                className="text-base text-gray-500 dark:text-gray-300 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200">
                {client?.phone}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">Email</p>
              <ButtonLink
                className="text-base"
                href={`mailto:${client?.email}`}>
                {client?.email}
              </ButtonLink>
            </div>
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2 md:self-start">
            <Button
              onClick={onEdit}
              variant="tertiary"
              leftIcon={<ModeEditOutlinedIcon sx={{ fontSize: '16px' }} />}>
              Edit
            </Button>
            <Button variant="tertiary" onlyIcon onClick={onDelete}>
              <DeleteOutlineOutlinedIcon sx={{ fontSize: '16px' }} />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
